import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { HiAcademicCap, HiBookOpen } from 'react-icons/hi'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair, seoBreadcrumbTriple } from '../../utils/seoUtils'
import './Certifications.css'

const certificationPrograms = {
  kubo: {
    name: 'КУБО',
    nameEn: 'KUBO',
    beltSystem: [
      { rank: '6 кю', color: 'Белый', colorEn: 'White', requirements: 'Базовые техники бросков и удержаний' },
      { rank: '5 кю', color: 'Желтый', colorEn: 'Yellow', requirements: 'Техники бросков, удержаний, базовые болевые приемы' },
      { rank: '4 кю', color: 'Оранжевый', colorEn: 'Orange', requirements: 'Расширенные техники бросков и удержаний' },
      { rank: '3 кю', color: 'Зеленый', colorEn: 'Green', requirements: 'Болевые и удушающие приемы, работа в партере' },
      { rank: '2 кю', color: 'Синий', colorEn: 'Blue', requirements: 'Комплексные техники, связки, работа против оружия' },
      { rank: '1 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Продвинутые техники, свободный спарринг' },
      { rank: '1 дан', color: 'Черный', colorEn: 'Black', requirements: 'Мастерство техники, преподавание' }
    ],
    terminology: [
      { term: 'Нагэ-вадза', meaning: 'Техники бросков', meaningEn: 'Throwing techniques' },
      { term: 'Осаэкоми-вадза', meaning: 'Техники удержаний', meaningEn: 'Holding techniques' },
      { term: 'Кансэцу-вадза', meaning: 'Болевые приемы', meaningEn: 'Joint lock techniques' },
      { term: 'Симе-вадза', meaning: 'Удушающие приемы', meaningEn: 'Choking techniques' },
      { term: 'Нэ-вадза', meaning: 'Техники в партере', meaningEn: 'Ground techniques' }
    ]
  },
  aikido: {
    name: 'Айкидо',
    nameEn: 'Aikido',
    beltSystem: [
      { rank: '6 кю', color: 'Белый', colorEn: 'White', requirements: 'Базовые стойки и перемещения' },
      { rank: '5 кю', color: 'Желтый', colorEn: 'Yellow', requirements: 'Иккё, базовые техники' },
      { rank: '4 кю', color: 'Оранжевый', colorEn: 'Orange', requirements: 'Никё, санкё, техники с оружием' },
      { rank: '3 кю', color: 'Зеленый', colorEn: 'Green', requirements: 'Йонкё, гокё, продвинутые техники' },
      { rank: '2 кю', color: 'Синий', colorEn: 'Blue', requirements: 'Роккё, нанакё, техники против нескольких противников' },
      { rank: '1 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Все техники, свободная практика' },
      { rank: '1 дан', color: 'Черный', colorEn: 'Black', requirements: 'Мастерство, преподавание, философия' }
    ],
    terminology: [
      { term: 'Иккё', meaning: 'Первый контроль', meaningEn: 'First control' },
      { term: 'Никё', meaning: 'Второй контроль', meaningEn: 'Second control' },
      { term: 'Санкё', meaning: 'Третий контроль', meaningEn: 'Third control' },
      { term: 'Йонкё', meaning: 'Четвертый контроль', meaningEn: 'Fourth control' },
      { term: 'Котэ-гаэси', meaning: 'Обратный захват запястья', meaningEn: 'Wrist reversal' },
      { term: 'Сихо-нагэ', meaning: 'Бросок на четыре стороны', meaningEn: 'Four-direction throw' }
    ]
  },
  jujutsu: {
    name: 'Дзю-дзюцу',
    nameEn: 'Jujutsu',
    beltSystem: [
      { rank: '6 кю', color: 'Белый', colorEn: 'White', requirements: 'Базовые стойки, падения, простые броски' },
      { rank: '5 кю', color: 'Желтый', colorEn: 'Yellow', requirements: 'Базовые броски и удержания' },
      { rank: '4 кю', color: 'Оранжевый', colorEn: 'Orange', requirements: 'Болевые приемы, техники с оружием' },
      { rank: '3 кю', color: 'Зеленый', colorEn: 'Green', requirements: 'Удушающие приемы, работа в партере' },
      { rank: '2 кю', color: 'Синий', colorEn: 'Blue', requirements: 'Комплексные техники, связки' },
      { rank: '1 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Продвинутые техники, свободный спарринг' },
      { rank: '1 дан', color: 'Черный', colorEn: 'Black', requirements: 'Мастерство, знание традиций' }
    ],
    terminology: [
      { term: 'Атэми', meaning: 'Удары', meaningEn: 'Strikes' },
      { term: 'Нагэ-вадза', meaning: 'Техники бросков', meaningEn: 'Throwing techniques' },
      { term: 'Катамэ-вадза', meaning: 'Техники удержаний', meaningEn: 'Holding techniques' },
      { term: 'Кансэцу-вадза', meaning: 'Болевые приемы', meaningEn: 'Joint lock techniques' },
      { term: 'Симе-вадза', meaning: 'Удушающие приемы', meaningEn: 'Choking techniques' }
    ]
  },
  aikijutsu: {
    name: 'Айки-дзюцу',
    nameEn: 'Aiki-jutsu',
    beltSystem: [
      { rank: '6 кю', color: 'Белый', colorEn: 'White', requirements: 'Базовые стойки, перемещения, принципы айки' },
      { rank: '5 кю', color: 'Желтый', colorEn: 'Yellow', requirements: 'Иккё, базовые броски и болевые приемы' },
      { rank: '4 кю', color: 'Оранжевый', colorEn: 'Orange', requirements: 'Никё, санкё, техники с оружием' },
      { rank: '3 кю', color: 'Зеленый', colorEn: 'Green', requirements: 'Йонкё, продвинутые техники, атэми' },
      { rank: '2 кю', color: 'Синий', colorEn: 'Blue', requirements: 'Комплексные техники, работа против нескольких противников' },
      { rank: '1 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Все техники, свободная практика' },
      { rank: '1 дан', color: 'Черный', colorEn: 'Black', requirements: 'Мастерство, преподавание, философия' }
    ],
    terminology: [
      { term: 'Иккё', meaning: 'Первый контроль', meaningEn: 'First control' },
      { term: 'Никё', meaning: 'Второй контроль', meaningEn: 'Second control' },
      { term: 'Санкё', meaning: 'Третий контроль', meaningEn: 'Third control' },
      { term: 'Атэми', meaning: 'Удары по жизненно важным точкам', meaningEn: 'Strikes to vital points' },
      { term: 'Кузуши', meaning: 'Нарушение равновесия', meaningEn: 'Breaking balance' }
    ]
  },
  aikibujutsu: {
    name: 'Айки-бу-дзюцу',
    nameEn: 'Aiki-bu-jutsu',
    beltSystem: [
      { rank: '6 кю', color: 'Белый', colorEn: 'White', requirements: 'Базовые техники айкидо, стойки с оружием' },
      { rank: '5 кю', color: 'Желтый', colorEn: 'Yellow', requirements: 'Базовые техники с дзё (палка)' },
      { rank: '4 кю', color: 'Оранжевый', colorEn: 'Orange', requirements: 'Техники с боккэном (деревянный меч)' },
      { rank: '3 кю', color: 'Зеленый', colorEn: 'Green', requirements: 'Техники с танто (нож), связки' },
      { rank: '2 кю', color: 'Синий', colorEn: 'Blue', requirements: 'Комплексные техники, работа против оружия' },
      { rank: '1 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Продвинутые техники, свободная практика' },
      { rank: '1 дан', color: 'Черный', colorEn: 'Black', requirements: 'Мастерство, преподавание' }
    ],
    terminology: [
      { term: 'Дзё-вадза', meaning: 'Техники с палкой', meaningEn: 'Staff techniques' },
      { term: 'Боккэн-вадза', meaning: 'Техники с деревянным мечом', meaningEn: 'Wooden sword techniques' },
      { term: 'Танто-вадза', meaning: 'Техники с ножом', meaningEn: 'Knife techniques' },
      { term: 'Тай-дзюцу', meaning: 'Техники без оружия', meaningEn: 'Unarmed techniques' },
      { term: 'Рэнраку-вадза', meaning: 'Связки техник', meaningEn: 'Technique combinations' }
    ]
  },
  karatejutsu: {
    name: 'Карате-дзюцу',
    nameEn: 'Karate-jutsu',
    beltSystem: [
      { rank: '10 кю', color: 'Белый', colorEn: 'White', requirements: 'Базовые стойки, удары, блоки' },
      { rank: '9 кю', color: 'Белый с полоской', colorEn: 'White with stripe', requirements: 'Первая ката, базовые техники' },
      { rank: '8 кю', color: 'Желтый', colorEn: 'Yellow', requirements: 'Базовые ката, кумитэ' },
      { rank: '7 кю', color: 'Оранжевый', colorEn: 'Orange', requirements: 'Продвинутые ката, техники ударов' },
      { rank: '6 кю', color: 'Зеленый', colorEn: 'Green', requirements: 'Средние ката, спарринг' },
      { rank: '5 кю', color: 'Синий', colorEn: 'Blue', requirements: 'Высокие ката, комплексные техники' },
      { rank: '4 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Продвинутые ката, свободный спарринг' },
      { rank: '3 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Мастерские ката, техники с оружием' },
      { rank: '2 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Высшие ката, преподавание' },
      { rank: '1 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Мастерство, философия' },
      { rank: '1 дан', color: 'Черный', colorEn: 'Black', requirements: 'Мастерство, преподавание' }
    ],
    terminology: [
      { term: 'Цуки', meaning: 'Удары руками', meaningEn: 'Hand strikes' },
      { term: 'Гэри', meaning: 'Удары ногами', meaningEn: 'Kicks' },
      { term: 'Укэ', meaning: 'Блоки', meaningEn: 'Blocks' },
      { term: 'Ката', meaning: 'Формальные комплексы', meaningEn: 'Formal patterns' },
      { term: 'Кумитэ', meaning: 'Спарринг', meaningEn: 'Sparring' },
      { term: 'Нагэ-вадза', meaning: 'Техники бросков', meaningEn: 'Throwing techniques' }
    ]
  },
  kenjutsu: {
    name: 'Кэн-дзюцу',
    nameEn: 'Kenjutsu',
    beltSystem: [
      { rank: '6 кю', color: 'Белый', colorEn: 'White', requirements: 'Базовые стойки, хват меча, простые удары' },
      { rank: '5 кю', color: 'Желтый', colorEn: 'Yellow', requirements: 'Базовые ката, техники ударов' },
      { rank: '4 кю', color: 'Оранжевый', colorEn: 'Orange', requirements: 'Продвинутые ката, блоки' },
      { rank: '3 кю', color: 'Зеленый', colorEn: 'Green', requirements: 'Комплексные техники, связки' },
      { rank: '2 кю', color: 'Синий', colorEn: 'Blue', requirements: 'Продвинутые ката, работа против нескольких противников' },
      { rank: '1 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Мастерские техники, свободная практика' },
      { rank: '1 дан', color: 'Черный', colorEn: 'Black', requirements: 'Мастерство, преподавание' }
    ],
    terminology: [
      { term: 'Сюри-кэн', meaning: 'Прямые удары', meaningEn: 'Straight cuts' },
      { term: 'Кэса-гири', meaning: 'Диагональные удары', meaningEn: 'Diagonal cuts' },
      { term: 'Цуки', meaning: 'Колющие удары', meaningEn: 'Thrusts' },
      { term: 'Укэ-нагаси', meaning: 'Отводы', meaningEn: 'Deflections' },
      { term: 'Иай', meaning: 'Техники выхватывания меча', meaningEn: 'Sword drawing techniques' },
      { term: 'Ката', meaning: 'Формальные комплексы', meaningEn: 'Formal patterns' }
    ]
  },
  kempo: {
    name: 'Кемпо',
    nameEn: 'Kempo',
    beltSystem: [
      { rank: '6 кю', color: 'Белый', colorEn: 'White', requirements: 'Базовые стойки, удары руками и ногами' },
      { rank: '5 кю', color: 'Желтый', colorEn: 'Yellow', requirements: 'Базовые блоки, простые комбинации' },
      { rank: '4 кю', color: 'Оранжевый', colorEn: 'Orange', requirements: 'Продвинутые удары, техники самообороны' },
      { rank: '3 кю', color: 'Зеленый', colorEn: 'Green', requirements: 'Броски, болевые приемы' },
      { rank: '2 кю', color: 'Синий', colorEn: 'Blue', requirements: 'Комплексные техники, связки' },
      { rank: '1 кю', color: 'Коричневый', colorEn: 'Brown', requirements: 'Продвинутые техники, свободный спарринг' },
      { rank: '1 дан', color: 'Черный', colorEn: 'Black', requirements: 'Мастерство, преподавание' }
    ],
    terminology: [
      { term: 'Тэ-вадза', meaning: 'Удары руками', meaningEn: 'Hand strikes' },
      { term: 'Аси-вадза', meaning: 'Удары ногами', meaningEn: 'Kicks' },
      { term: 'Укэ-вадза', meaning: 'Блоки', meaningEn: 'Blocks' },
      { term: 'Нагэ-вадза', meaning: 'Техники бросков', meaningEn: 'Throwing techniques' },
      { term: 'Кансэцу-вадза', meaning: 'Болевые приемы', meaningEn: 'Joint lock techniques' }
    ]
  }
}

function Certifications() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const { disciplineId } = useParams()
  const navigate = useNavigate()
  const [selectedDiscipline, setSelectedDiscipline] = useState(disciplineId || 'all')

  // Синхронизируем состояние с URL параметром
  useEffect(() => {
    setSelectedDiscipline(disciplineId || 'all')
  }, [disciplineId])

  const isRussian = i18n.language === 'ru'
  const isJapanese = i18n.language === 'ja'

  const getDisciplineName = (id) => {
    const names = {
      kubo: { ru: 'КУБО', en: 'KUBO', ja: 'КУБО' },
      aikido: { ru: 'Айкидо', en: 'Aikido', ja: '合気道' },
      aikijutsu: { ru: 'Айки-дзюцу', en: 'Aiki-jutsu', ja: '合気術' },
      jujutsu: { ru: 'Дзю-дзюцу', en: 'Jujutsu', ja: '柔術' },
      aikibujutsu: { ru: 'Айки-бу-дзюцу', en: 'Aiki-bu-jutsu', ja: '合気武術' },
      karatejutsu: { ru: 'Карате-дзюцу', en: 'Karate-jutsu', ja: '空手術' },
      kenjutsu: { ru: 'Кэн-дзюцу', en: 'Kenjutsu', ja: '剣術' },
      kempo: { ru: 'Кемпо', en: 'Kempo', ja: '拳法' }
    }
    if (id === 'all') return t('certifications.all', 'All Disciplines')
    const lang = isJapanese ? 'ja' : (isRussian ? 'ru' : 'en')
    return names[id] ? names[id][lang] : id
  }

  const disciplines = [
    { id: 'all', name: t('certifications.all', 'All Disciplines') },
    { id: 'kubo', name: getDisciplineName('kubo') },
    { id: 'aikido', name: getDisciplineName('aikido') },
    { id: 'aikijutsu', name: getDisciplineName('aikijutsu') },
    { id: 'jujutsu', name: getDisciplineName('jujutsu') },
    { id: 'aikibujutsu', name: getDisciplineName('aikibujutsu') },
    { id: 'karatejutsu', name: getDisciplineName('karatejutsu') },
    { id: 'kenjutsu', name: getDisciplineName('kenjutsu') },
    { id: 'kempo', name: getDisciplineName('kempo') }
  ]

  const selectedProgram = selectedDiscipline !== 'all' ? certificationPrograms[selectedDiscipline] : null

  const breadcrumbItems = useMemo(() => {
    const home = t('common.home')
    const cert = t('common.certifications')
    if (!disciplineId || selectedDiscipline === 'all') {
      return seoBreadcrumbPair(home, cert, 'certifications')
    }
    return seoBreadcrumbTriple(
      home,
      cert,
      'certifications',
      getDisciplineName(disciplineId),
      `certifications/${disciplineId}`
    )
  }, [t, disciplineId, selectedDiscipline, isJapanese, isRussian])

  return (
    <div className="certifications-page">
      <SEO 
        title={t('common.certifications')}
        description={t('certifications.description', 'Belt certification programs in SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="certifications-hero">
          <h1>{t('common.certifications')}</h1>
          <p className="certifications-intro">
            {t('certifications.intro', 'SUMA International offers comprehensive belt certification programs for various martial arts disciplines. Each program includes technical requirements, theory, and physical training.')}
          </p>
        </div>

        <div className="certifications-filter">
          <select
            value={selectedDiscipline}
            onChange={(e) => {
              const newDiscipline = e.target.value
              setSelectedDiscipline(newDiscipline)
              if (newDiscipline === 'all') {
                navigate(getLocalizedLink('/certifications'))
              } else {
                navigate(`${getLocalizedLink('/certifications')}/${newDiscipline}`)
              }
            }}
            className="discipline-select"
          >
            {disciplines.map(discipline => (
              <option key={discipline.id} value={discipline.id}>
                {discipline.name}
              </option>
            ))}
          </select>
        </div>

        {selectedProgram ? (
          <>
            <section className="certifications-section">
              <h2>{isJapanese ? (selectedProgram.nameJa || selectedProgram.nameEn) : (isRussian ? selectedProgram.name : selectedProgram.nameEn)} - {t('certifications.beltSystem', 'Belt System')}</h2>
              <div className="belt-system-table">
                <div className="table-header">
                  <div className="col-rank">{t('certifications.rank', 'Rank')}</div>
                  <div className="col-color">{t('certifications.color', 'Belt Color')}</div>
                  <div className="col-requirements">{t('certifications.requirements', 'Requirements')}</div>
                </div>
                {selectedProgram.beltSystem.map((belt, index) => (
                  <div key={index} className="table-row">
                    <div className="col-rank">{belt.rank}</div>
                    <div className="col-color">{isJapanese ? (belt.colorJa || belt.colorEn) : (isRussian ? belt.color : belt.colorEn)}</div>
                    <div className="col-requirements">{isJapanese ? (belt.requirementsJa || belt.requirements) : (isRussian ? belt.requirements : (belt.requirementsEn || belt.requirements))}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="certifications-section">
              <h2>{t('certifications.terminology', 'Terminology')}</h2>
              <div className="terminology-grid">
                {selectedProgram.terminology.map((term, index) => (
                  <div key={index} className="term-card">
                    <h3>{term.term}</h3>
                    <p>{isJapanese ? (term.meaningJa || term.meaningEn) : (isRussian ? term.meaning : term.meaningEn)}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        ) : (
          <section className="certifications-section">
            <h2>{t('certifications.programs', 'Certification Programs')}</h2>
            <div className="programs-grid">
              <div className="program-card">
                <HiAcademicCap className="program-icon" />
                <h3>{t('certifications.beltSystem', 'Belt System')}</h3>
                <p>{t('certifications.beltSystemText', 'Each martial art has its own belt system with specific requirements for advancement.')}</p>
              </div>
              <div className="program-card">
                <HiBookOpen className="program-icon" />
                <h3>{t('certifications.terminology', 'Terminology')}</h3>
                <p>{t('certifications.terminologyText', 'Learn the terminology and concepts specific to each martial art discipline.')}</p>
              </div>
              <div className="program-card">
                <HiAcademicCap className="program-icon" />
                <h3>{t('certifications.requirements', 'Requirements')}</h3>
                <p>{t('certifications.requirementsText', 'Technical requirements, age limits, and experience prerequisites for each level.')}</p>
              </div>
            </div>
            <p className="select-discipline-hint">
              {t('certifications.selectDiscipline', 'Select a discipline from the filter above to view detailed certification programs and terminology.')}
            </p>
          </section>
        )}

        <section className="certifications-section">
          <h2>{t('certifications.process', 'Certification Process')}</h2>
          <div className="process-steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>{t('certifications.step1', 'Preparation')}</h3>
              <p>{t('certifications.step1Text', 'Study the requirements and prepare for the examination')}</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>{t('certifications.step2', 'Examination')}</h3>
              <p>{t('certifications.step2Text', 'Demonstrate technical skills, knowledge, and physical fitness')}</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>{t('certifications.step3', 'Certification')}</h3>
              <p>{t('certifications.step3Text', 'Receive official certification and belt rank')}</p>
            </div>
          </div>
        </section>

        <section className="certifications-section">
          <h2>{t('certifications.moreInfo', 'More Information')}</h2>
          <p>{t('certifications.moreInfoText', 'For detailed information about certification programs, requirements, and schedules, please contact your local representative or visit the Documents section.')}</p>
          <div className="cert-actions">
            <Link to={getLocalizedLink('/documents')} className="btn btn-primary">
              {t('common.documents')}
            </Link>
            <Link to={getLocalizedLink('/contact')} className="btn btn-secondary">
              {t('common.contact')}
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Certifications
